// src/components/downloads/DownloadsFilteredSection.jsx
import React, { useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { MagnifyingGlassIcon, TagIcon } from "@heroicons/react/24/outline";
import { PDFCard } from "./Brand";
import { flattenStrapiEntity } from "../../utils/strapiFlatten";
import { getManualCategorySlug, normalizeManualPathSegment } from "../../utils/manualCategory";
import {
  getBrandThemeClasses,
  resolveBrandThemeId,
} from "../../utils/brandThemeClasses";

/**
 * Strapi may return a plain array or `{ data: [...] }` for repeatable components.
 */
function normalizeFilterArray(value) {
  if (Array.isArray(value)) return value;
  if (value && Array.isArray(value.data)) return value.data;
  return [];
}

function resolveFilterFields(filterConfig) {
  if (!filterConfig) return {};
  if (Array.isArray(filterConfig)) return filterConfig[0] || {};
  return filterConfig;
}

/**
 * Build option values from `cas-downloads.filter.category`: each entry `listValue`.
 */
function mapFilterListValues(entries) {
  return normalizeFilterArray(entries)
    .map((entry) => flattenStrapiEntity(entry))
    .map((flat) => String(flat?.listValue ?? "").trim())
    .filter(Boolean);
}

function buildSelectOptions(values) {
  const unique = [...new Set(values)].sort((a, b) => a.localeCompare(b));
  return ["All", ...unique];
}

function getFileCategoryMatchValue(file) {
  if (!file || typeof file !== "object") return "";
  return String(
    file.categoryListValue ??
      file.category?.listValue ??
      file.category_listValue ??
      file.category ??
      ""
  ).trim();
}

/** Text blob used to match left "Category" dropdown (e.g. "Brochure" in title or filename). */
function getFileCategorySearchHaystack(file) {
  if (!file || typeof file !== "object") return "";
  const parts = [
    file.download_title,
    file.title,
    file.name,
    file.download_file?.name,
    file.download_file?.url,
    file.url,
    getFileCategoryMatchValue(file),
  ];
  return parts.filter(Boolean).join(" ").toLowerCase();
}

/**
 * When user picks e.g. "Brochure", show files whose title / file name / URL (or CMS category field) contains that text (case-insensitive). Exact structured match still counts.
 */
function fileMatchesCategoryFilter(file, selectedCategory) {
  if (!selectedCategory || selectedCategory === "All") return true;
  const needle = selectedCategory.trim().toLowerCase();
  if (!needle) return true;

  const structured = getFileCategoryMatchValue(file).toLowerCase();
  if (structured === needle) return true;

  const haystack = getFileCategorySearchHaystack(file);
  return haystack.includes(needle);
}

function resolveDownloadsThemeId(brand) {
  return resolveBrandThemeId(brand, getManualCategorySlug(brand));
}

const DownloadsFilteredSection = ({
  filterConfig,
  selectedBrand,
  brands = [],
  basePath = "/downloads",
}) => {
  const filterFields = useMemo(
    () => resolveFilterFields(filterConfig),
    [filterConfig]
  );

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchKey, setDebouncedSearchKey] = useState("");
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState("All");

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedSearchKey(searchQuery.trim().toLowerCase());
    }, 280);
    return () => window.clearTimeout(handle);
  }, [searchQuery]);

  const rawFiles = useMemo(() => {
    if (!selectedBrand) return [];
    return (
      selectedBrand.manual_files ||
      selectedBrand.download_files ||
      []
    );
  }, [selectedBrand]);

  const categoryOptions = useMemo(() => {
    const fromCms = mapFilterListValues(filterFields?.category);
    if (fromCms.length === 0) return ["All"];
    return buildSelectOptions(fromCms);
  }, [filterFields?.category]);

  useEffect(() => {
    if (
      selectedCategoryFilter !== "All" &&
      !categoryOptions.includes(selectedCategoryFilter)
    ) {
      setSelectedCategoryFilter("All");
    }
  }, [categoryOptions, selectedCategoryFilter]);

  useEffect(() => {
    setSelectedCategoryFilter("All");
    setSearchQuery("");
    setDebouncedSearchKey("");
  }, [selectedBrand?.id, selectedBrand?.documentId]);

  const filteredFiles = useMemo(() => {
    if (!rawFiles.length) return [];

    const searchTokens = debouncedSearchKey.split(/\s+/).filter(Boolean);

    return rawFiles.filter((file) => {
      if (!fileMatchesCategoryFilter(file, selectedCategoryFilter)) {
        return false;
      }

      if (searchTokens.length === 0) return true;

      const title = (file.download_title || file.title || "").toLowerCase();
      const fileUrl = (
        file.download_file?.url ||
        file.download_file?.name ||
        file.url ||
        file.name ||
        ""
      ).toLowerCase();
      const haystack = `${title} ${fileUrl}`;

      return searchTokens.every((t) => haystack.includes(t));
    });
  }, [rawFiles, selectedCategoryFilter, debouncedSearchKey]);

  const filterTitle = filterFields?.label || filterFields?.title || "Filter";
  const searchLabel = filterFields?.searchLabel || "Search";
  const searchPlaceholder =
    filterFields?.searchPlaceholder ||
    filterFields?.searchPlaceHolder ||
    filterFields?.serachPlaceHolder ||
    "Search files...";
  const categoryLabel = filterFields?.categoryLabel || "Category";

  const listAnimKey = `${selectedCategoryFilter}|${debouncedSearchKey}|${selectedBrand?.id || selectedBrand?.documentId || ""}`;

  const displayName =
    selectedBrand?.name || selectedBrand?.title || "Downloads";
  const displaySubtitle = selectedBrand?.subtitle || "";
  const logo = selectedBrand?.logo;
  const selectedThemeId = resolveDownloadsThemeId(selectedBrand);
  const selectedThemeClasses = getBrandThemeClasses(selectedThemeId);

  return (
    <section className="section-surface-lake w-full py-12 md:py-16 lg:py-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
        <aside className="lg:w-64 xl:w-72 lg:sticky lg:top-24 lg:self-start">
          <div className="panel-elevated rounded-2xl p-4 md:p-5 lg:p-6">
            <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-4 md:mb-5">
              {filterTitle}
            </h2>
            <div className="space-y-5">
              <div>
                <label
                  htmlFor="downloads-search"
                  className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3"
                >
                  <MagnifyingGlassIcon
                    className={`h-5 w-5 ${selectedThemeClasses.icon}`}
                  />
                  {searchLabel}
                </label>
                <input
                  id="downloads-search"
                  type="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={searchPlaceholder}
                  autoComplete="off"
                  className={selectedThemeClasses.input}
                />
              </div>

              <div>
                <label
                  htmlFor="downloads-category-filter"
                  className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3"
                >
                  <TagIcon className={`h-5 w-5 ${selectedThemeClasses.icon}`} />
                  {categoryLabel}
                </label>
                <select
                  id="downloads-category-filter"
                  value={selectedCategoryFilter}
                  onChange={(e) => setSelectedCategoryFilter(e.target.value)}
                  className={selectedThemeClasses.select}
                >
                  {categoryOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </aside>

        <div className="flex-1">
          {brands.length > 0 && (
            <div
              className="relative z-10 mb-0 flex flex-wrap items-end gap-0.5 sm:gap-1 pl-1 pr-1 sm:pl-2 sm:pr-2 after:pointer-events-none after:absolute after:inset-x-0 after:bottom-0 after:z-0 after:h-px after:bg-mist-200/70"
              role="tablist"
              aria-label="Download brands"
            >
              {brands.map((brand) => {
                const slug = getManualCategorySlug(brand);
                const tabLabel = brand.name || brand.title || slug || "Brand";
                const selectedSlug = selectedBrand
                  ? getManualCategorySlug(selectedBrand)
                  : "";
                const tabThemeId = resolveDownloadsThemeId(brand);
                const tabThemeClasses = getBrandThemeClasses(tabThemeId);
                const isActive =
                  !!slug &&
                  !!selectedSlug &&
                  normalizeManualPathSegment(selectedSlug) ===
                    normalizeManualPathSegment(slug);

                if (!slug) return null;

                return (
                  <Link
                    key={brand.id || brand.documentId || slug}
                    to={`${basePath}/${encodeURIComponent(slug)}`}
                    role="tab"
                    aria-selected={isActive}
                    className={`inline-flex min-w-[4.5rem] items-center justify-center whitespace-nowrap text-sm sm:text-[0.9375rem] tracking-tight transition-all duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-gray-400/80 ${
                      isActive
                        ? tabThemeClasses.tabActive
                        : `${tabThemeClasses.tabInactive} hover:-translate-y-0.5`
                    }`}
                  >
                    {tabLabel}
                  </Link>
                );
              })}
            </div>
          )}

          <div
            className={`${selectedThemeClasses.panel} p-6 transition-all duration-300 hover:shadow-2xl md:p-8 lg:p-10`}
            key={selectedBrand?.id || selectedBrand?.documentId || "brand"}
            role="tabpanel"
          >
            {(displayName || (logo && logo.url)) && (
              <div className="mb-6 md:mb-8 flex items-center justify-between gap-4">
                <div>
                  {displayName && (
                    <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
                      {displayName}
                    </h2>
                  )}
                  {displaySubtitle && (
                    <p className="mt-1 text-sm sm:text-base text-gray-500">
                      {displaySubtitle}
                    </p>
                  )}
                </div>
                {logo && logo.url && (
                  <div className="ml-4 flex-shrink-0 flex items-center justify-center">
                    <img
                      src={logo.url}
                      alt={logo.alternativeText || logo.name || "Brand logo"}
                      className="h-12 sm:h-14 md:h-16 w-auto object-contain"
                    />
                  </div>
                )}
              </div>
            )}

            <div className="space-y-4 md:space-y-6" key={listAnimKey}>
              {filteredFiles.length === 0 ? (
                <div className="text-center py-8 text-gray-500 support-tab-fade-in">
                  <p>
                    {rawFiles.length === 0
                      ? "No files available for this brand."
                      : "No files match your filters or search."}
                  </p>
                </div>
              ) : (
                filteredFiles.map((file, fileIndex) => (
                  <div
                    key={file.id || fileIndex}
                    className="support-media-card-fade-in"
                    style={{
                      "--support-media-delay": `${fileIndex * 60}ms`,
                    }}
                  >
                    <PDFCard
                      downloadFile={file}
                      rowIndex={fileIndex}
                      themeId={selectedThemeId}
                    />
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
      </div>
    </section>
  );
};

export default DownloadsFilteredSection;
